import BackButton from "@dashboard/components/BackButton";
import { ConfirmButton } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import { Box, Checkbox, Input } from "@saleor/macaw-ui-next";
import React, { useEffect,useState } from "react";
import { FormattedMessage } from "react-intl";

import { useUpdateInquiryFormMetadataMutation,useUpdateInquiryFormMutation } from "../hooks";

interface InquiryFormEditModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  form?: {
    id: string;
    title: string;
    slug: string;
    isPublished: boolean;
    metadata?: Array<{
      key: string;
      value: string;
    }>;
  };
}

interface FormData {
  title: string;
  slug: string;
  hidden: boolean;
  emailTo: string;
  emailCc: string;
  emailBcc: string;
  emailSubject: string;
  description: string;
  successMessage: string;
  fields: string;
  contactForm: boolean;
  productInquiryForm: boolean;
  reCAPTCHA: boolean;
}

const InquiryFormEditModal: React.FC<InquiryFormEditModalProps> = ({
  open,
  onClose,
  onSuccess,
  form,
}) => {
  const [updateInquiryForm, { loading }] = useUpdateInquiryFormMutation();
  const [updateMetadata, { loading: updateLoading }] = useUpdateInquiryFormMetadataMutation();

  const [formData, setFormData] = useState<FormData>({
    title: "",
    slug: "",
    hidden: false,
    emailTo: "",
    emailCc: "",
    emailBcc: "",
    emailSubject: "New Inquiry Received",
    description: "Fill this form to get in touch with us.",
    successMessage: "Thank you for reaching out. We'll respond shortly!",
    fields: "name, email, message",
    contactForm: false,
    productInquiryForm: false,
    reCAPTCHA: false,
  });

  useEffect(() => {
    if (form) {
      const getMetadataValue = (key: string, defaultValue: string = "") => {
        const metadataItem = form.metadata?.find(item => item.key === key);

        return metadataItem?.value || defaultValue;
      };

      setFormData({
        title: form.title,
        slug: form.slug,
        hidden: !form.isPublished,
        emailTo: getMetadataValue("email_to"),
        emailCc: getMetadataValue("email_cc"),
        emailBcc: getMetadataValue("email_bcc"),
        emailSubject: getMetadataValue("email_subject", "New Inquiry Received"),
        description: getMetadataValue("description", "Fill this form to get in touch with us."),
        successMessage: getMetadataValue("success_message", "Thank you for reaching out. We'll respond shortly!"),
        fields: getMetadataValue("fields", "name, email, message"),
        contactForm: getMetadataValue("contact_form") === "true",
        productInquiryForm: getMetadataValue("product_inquiry_form") === "true",
        reCAPTCHA: getMetadataValue("reCAPTCHA") === "true",
      });
    }
  }, [form]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const handleTitleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value,
      slug: generateSlug(value),
    }));
  };

  const handleSubmit = async () => {
    if (!form?.id || !formData.title) return;

    try {
      // Step 1: Update basic page information
      await updateInquiryForm({
        variables: {
          id: form.id,
          input: {
            title: formData.title,
            slug: formData.slug,
            isPublished: !formData.hidden,
          }
        }
      });

      // Step 2: Update metadata
      await updateMetadata({
        variables: {
          id: form.id,
          input: [
            { key: "email_to", value: formData.emailTo },
            { key: "email_cc", value: formData.emailCc },
            { key: "email_bcc", value: formData.emailBcc },
            { key: "email_subject", value: formData.emailSubject },
            { key: "description", value: formData.description },
            { key: "success_message", value: formData.successMessage },
            { key: "fields", value: formData.fields },
            { key: "contact_form", value: formData.contactForm.toString() },
            { key: "product_inquiry_form", value: formData.productInquiryForm.toString() },
            { key: "reCAPTCHA", value: formData.reCAPTCHA.toString() },
          ]
        }
      });

      onSuccess();
    } catch (error) {
      console.error("Error updating inquiry form:", error);
    }
  };

  const handleClose = () => {
    onClose();
    // Form will be reset by useEffect when form prop changes
  };

  const isLoading = loading || updateLoading;

  return (
    <DashboardModal onChange={handleClose} open={open}>
      <DashboardModal.Content size="md">
        <DashboardModal.Header>
          <FormattedMessage
            id="8fWEQj"
            defaultMessage="Edit Inquiry Form"
          />
        </DashboardModal.Header>

        <Box display="flex" flexDirection="column" gap={4}>
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            required
            disabled={isLoading}
          />
          
          <Input
            label="Slug"
            value={formData.slug}
            onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
            required
            disabled={true}
          />

          <Checkbox
            checked={formData.hidden}
            onCheckedChange={(checked) => 
              setFormData(prev => ({ ...prev, hidden: !!checked }))
            }
            disabled={isLoading}
          >
            Hidden
          </Checkbox>

          <Input
            label="Email To"
            value={formData.emailTo}
            onChange={(e) => setFormData(prev => ({ ...prev, emailTo: e.target.value }))}
            disabled={isLoading}
          />

          <Input
            label="Email CC"
            value={formData.emailCc}
            onChange={(e) => setFormData(prev => ({ ...prev, emailCc: e.target.value }))}
            disabled={isLoading}
          />

          <Input
            label="Email BCC"
            value={formData.emailBcc}
            onChange={(e) => setFormData(prev => ({ ...prev, emailBcc: e.target.value }))}
            disabled={isLoading}
          />

          <Input
            label="Email Subject"
            value={formData.emailSubject}
            onChange={(e) => setFormData(prev => ({ ...prev, emailSubject: e.target.value }))}
            disabled={isLoading}
          />

          <Input
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            disabled={isLoading}
          />

          <Input
            label="Success Message"
            value={formData.successMessage}
            onChange={(e) => setFormData(prev => ({ ...prev, successMessage: e.target.value }))}
            disabled={isLoading}
          />

          <Input
            label="Fields"
            value={formData.fields}
            onChange={(e) => setFormData(prev => ({ ...prev, fields: e.target.value }))}
            disabled={isLoading}
            helperText="Comma-separated list of fields"
          />

          <Checkbox
            checked={formData.contactForm}
            onCheckedChange={(checked) => 
              setFormData(prev => ({ ...prev, contactForm: !!checked }))
            }
            disabled={isLoading}
          >
            Contact Form
          </Checkbox>

          <Checkbox
            checked={formData.productInquiryForm}
            onCheckedChange={(checked) =>
              setFormData(prev => ({ ...prev, productInquiryForm: !!checked }))
            }
            disabled={isLoading}
          >
            Product Inquiry Form
          </Checkbox>

          <Checkbox
            checked={formData.reCAPTCHA}
            onCheckedChange={(checked) =>
              setFormData(prev => ({ ...prev, reCAPTCHA: !!checked }))
            }
            disabled={isLoading}
          >
            reCAPTCHA
          </Checkbox>
        </Box>

        <DashboardModal.Actions>
          <BackButton onClick={handleClose} />
          <ConfirmButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!formData.title || isLoading}
            transitionState={isLoading ? "loading" : "default"}
          >
            <FormattedMessage id="jvo0vs" defaultMessage="Save" />
          </ConfirmButton>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default InquiryFormEditModal;